import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star, Instagram, Music2, MessageCircle, Phone, Globe, UtensilsCrossed,
  MapPin, Navigation, Contact2, Link2, Upload, ArrowLeft, ArrowRight, Check, Sparkles,
} from "lucide-react";
import Seo from "../components/Seo";
import CardMockup, { CardPair } from "../components/CardMockup";
import SurMesure from "../components/SurMesure";
import { WhatsAppIcon } from "../components/Cta";
import { ACTIVITIES, OFFERS, waLink, RESPONSE_TIME } from "../config/site";
import { EASE } from "../components/motion";
import { gradientFrom, readableOn, normalizeHex } from "../lib/colors";

const LINK_CATALOG = [
  { key: "google", label: "Google Avis", icon: Star, placeholder: "https://g.page/votre-fiche" },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/votrecompte" },
  { key: "tiktok", label: "TikTok", icon: Music2, placeholder: "https://tiktok.com/@votrecompte" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, placeholder: "+33 6 12 34 56 78" },
  { key: "telephone", label: "Téléphone", icon: Phone, placeholder: "+33 1 23 45 67 89" },
  { key: "site", label: "Site web", icon: Globe, placeholder: "https://votresite.fr" },
  { key: "menu", label: "Menu / carte", icon: UtensilsCrossed, placeholder: "https://votresite.fr/menu" },
  { key: "adresse", label: "Adresse", icon: MapPin, placeholder: "12 rue des Lilas, 75011 Paris" },
  { key: "itineraire", label: "Itinéraire", icon: Navigation, placeholder: "https://maps.google.com/…" },
  { key: "contact", label: "Fiche contact", icon: Contact2, placeholder: "Nom, fonction, e-mail…" },
  { key: "autre", label: "Autre lien", icon: Link2, placeholder: "https://…" },
];

const SUGGESTIONS = {
  Restaurant: ["google", "instagram", "menu", "whatsapp"],
  Café: ["google", "instagram", "menu", "whatsapp"],
  Bar: ["google", "instagram", "tiktok", "whatsapp"],
  Coiffeur: ["instagram", "google", "telephone", "whatsapp"],
  Barbier: ["tiktok", "instagram", "google", "whatsapp"],
  "Institut de beauté": ["instagram", "google", "site", "whatsapp"],
  Boutique: ["instagram", "whatsapp", "itineraire", "google"],
  Garage: ["telephone", "whatsapp", "itineraire", "google"],
  Hôtel: ["site", "google", "whatsapp", "itineraire"],
  Immobilier: ["contact", "whatsapp", "site", "google"],
  Artisan: ["whatsapp", "telephone", "google", "instagram"],
  Coach: ["instagram", "whatsapp", "site", "contact"],
  Entreprise: ["contact", "site", "google", "whatsapp"],
  Autre: ["google", "instagram", "site"],
};

// Palettes suggérées — simples points de départ : la couleur reste 100 % libre.
const PALETTES = [
  { label: "Noir & or", base: "#0A0A0A", accent: "#C9A96E" },
  { label: "Blanc & bleu", base: "#F7FAFF", accent: "#1677FF" },
  { label: "Bleu nuit", base: "#071A2D", accent: "#4C9AFF" },
  { label: "Bleu électrique", base: "#1677FF", accent: "#BFD9FF" },
  { label: "Bordeaux", base: "#6E0E0E", accent: "#F2C94C" },
  { label: "Vert forêt", base: "#0B2B1A", accent: "#C9A96E" },
  { label: "Rose poudré", base: "#F2D5DE", accent: "#A63A5B" },
  { label: "Violet nuit", base: "#2A1B4A", accent: "#C4B5FD" },
  { label: "Terracotta", base: "#B4552D", accent: "#FFE3C2" },
  { label: "Anthracite", base: "#23272E", accent: "#9FB8D8" },
  { label: "Crème & cacao", base: "#F5EDE0", accent: "#6B4F2E" },
  { label: "Corail", base: "#E8503A", accent: "#FFE9D6" },
];

const STYLES = [
  { id: "epure", label: "Épuré", desc: "Cadre fin, ornement discret", pattern: null },
  { id: "premium", label: "Premium", desc: "Cadre foil et ornements", pattern: "arcs" },
  { id: "grand-luxe", label: "Grand luxe", desc: "Double cadre foil, ornements d'angle", pattern: "arcs", luxe: true },
  { id: "noir-or", label: "Élégant noir & or", desc: "Noir profond, foil doré", pattern: null, colors: ["#0A0A0A", "#C9A96E"] },
  { id: "colore", label: "Moderne coloré", desc: "Motif léger en filigrane", pattern: "vagues" },
];

// Motifs de fond illustrés — proposés automatiquement selon l'activité.
const MOTIFS = [
  { id: "auto", label: "Automatique" },
  { id: "aucun", label: "Aucun" },
  { id: "arcs", label: "Arcs" },
  { id: "vagues", label: "Vagues" },
  { id: "gastronomie", label: "Gastronomie" },
  { id: "botanique", label: "Botanique" },
];
const MOTIF_BY_ACTIVITY = {
  Restaurant: "gastronomie",
  Café: "gastronomie",
  Bar: "gastronomie",
  "Institut de beauté": "botanique",
};

const STEPS = ["Activité", "Personnalisation", "Contenu digital", "Aperçu", "Récapitulatif"];

const inputCls =
  "w-full rounded-xl border border-nuit-900/15 bg-white px-4 py-3 text-sm text-nuit-900 placeholder:text-steel/60 transition-colors focus:border-electric focus:outline-none";

export default function Configurateur() {
  const [step, setStep] = useState(0);
  const [activity, setActivity] = useState("");
  const [name, setName] = useState("");
  const [slogan, setSlogan] = useState("");
  const [baseHex, setBaseHex] = useState("#071A2D");
  const [accentHex, setAccentHex] = useState("#4C9AFF");
  const [styleId, setStyleId] = useState("premium");
  const [motifSel, setMotifSel] = useState("auto");
  const [surMesure, setSurMesure] = useState(true);
  const [logo, setLogo] = useState(null);
  const [logoError, setLogoError] = useState("");
  const [links, setLinks] = useState({});
  const [offerId, setOfferId] = useState(null);
  const fileRef = useRef(null);
  const topRef = useRef(null);

  const base = normalizeHex(baseHex) || "#071A2D";
  const accent = normalizeHex(accentHex) || "#4C9AFF";
  const style = STYLES.find((s) => s.id === styleId);
  const motifAuto = MOTIF_BY_ACTIVITY[activity] || style.pattern;
  const pattern = motifSel === "auto" ? motifAuto : motifSel === "aucun" ? null : motifSel;
  const motifLabel = MOTIFS.find((m) => m.id === pattern)?.label || null;
  const bg = gradientFrom(base);
  const fg = readableOn(base);
  const activeLinks = LINK_CATALOG.filter((l) => links[l.key]?.enabled);
  const offer = OFFERS.find((o) => o.id === offerId) || null;
  const suggested = SUGGESTIONS[activity] || [];

  const cardProps = { name: name || "Votre Établissement", slogan, bg, fg, accent, logo, pattern, patternColor: accent, luxe: !!style.luxe };

  const pickActivity = (a) => {
    setActivity(a);
    const next = { ...links };
    (SUGGESTIONS[a] || []).forEach((k) => {
      if (!next[k]) next[k] = { enabled: true, value: "" };
    });
    setLinks(next);
  };

  const pickStyle = (s) => {
    setStyleId(s.id);
    if (s.colors) {
      setBaseHex(s.colors[0]);
      setAccentHex(s.colors[1]);
    }
  };

  const toggleLink = (key) =>
    setLinks((p) => ({ ...p, [key]: { enabled: !p[key]?.enabled, value: p[key]?.value || "" } }));
  const setLinkValue = (key, value) =>
    setLinks((p) => ({ ...p, [key]: { enabled: p[key]?.enabled ?? true, value } }));

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    setLogoError("");
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/svg+xml"].includes(f.type)) {
      setLogoError("Format accepté : PNG, JPG ou SVG (fond transparent conseillé).");
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      setLogoError("Fichier trop lourd : 3 Mo maximum pour l'aperçu.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(f);
  };

  const canContinue =
    (step === 0 && !!activity) ||
    (step === 1 && name.trim().length >= 2) ||
    (step === 2 && activeLinks.length >= 1) ||
    step === 3 ||
    step === 4;

  const goTo = (n) => {
    setStep(n);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const effectiveOffer = offer || (activeLinks.length > 1 ? OFFERS[1] : OFFERS[0]);
  const quantity = effectiveOffer.id === "pack4" ? "4 cartes" : "1 carte";

  const recapMessage = useMemo(() => {
    const lines = [
      "Bonjour Noveocard, je souhaite commander ma carte digitale personnalisée.",
      "",
      `— Type de carte : ${effectiveOffer.name} (${effectiveOffer.price})`,
      `— Quantité : ${quantity}`,
      `— Activité : ${activity || "à préciser"}`,
      `— Nom sur la carte : ${name || "à préciser"}`,
      slogan ? `— Slogan : ${slogan}` : null,
      `— Couleur de carte : ${base}`,
      `— Couleur secondaire : ${accent}`,
      `— Style : ${style.label}`,
      motifLabel ? `— Motif de fond : ${motifLabel}` : "— Motif de fond : aucun",
      `— Personnalisation sur-mesure par l'équipe Noveocard : ${surMesure ? "Oui" : "Non"}`,
      logo
        ? "— Logo : je vous l'envoie dans cette conversation (fichier prêt)"
        : "— Logo : je vous l'enverrai dans cette conversation",
      "",
      "Liens souhaités :",
      ...activeLinks.map((l) => `· ${l.label}${l.value ? ` : ${l.value}` : ""}`),
    ].filter(Boolean);
    return lines.join("\n");
  }, [effectiveOffer, quantity, activity, name, slogan, base, accent, style, motifLabel, surMesure, logo, activeLinks]);

  return (
    <>
      <Seo
        title="Configurateur — Créez votre carte digitale"
        description="Configurez votre carte digitale Noveocard : couleur libre, logo, style premium, liens. Aperçu recto-verso en temps réel, commande sur WhatsApp. À partir de 40 € TTC."
        path="/personnalisation"
      />

      <section ref={topRef} className="bg-brume pb-32 pt-28 sm:pt-32">
        <div className="container-nv max-w-3xl">
          <p className="eyebrow">Configurateur</p>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-5xl">
            Créez la vôtre, en cinq gestes.
          </h1>
          <p className="mt-4 text-steel">
            Couleur libre, votre logo, finition premium : votre carte ne se limite pas à un logo sur
            une couleur — chaque carte est dessinée sur-mesure. La commande se finalise sur WhatsApp,
            avec tout votre récapitulatif déjà préparé.
          </p>

          <ol className="mt-10 flex items-center gap-2" aria-label="Étapes du configurateur">
            {STEPS.map((label, i) => (
              <li key={label} className="flex flex-1 flex-col items-center gap-2">
                <span
                  className={`step-dot w-full border-0 ${
                    i < step ? "bg-electric text-white" : i === step ? "bg-nuit-900 text-white" : "bg-white text-steel"
                  }`}
                  style={{ borderRadius: 999 }}
                  aria-current={i === step ? "step" : undefined}
                >
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className={`hidden text-[11px] font-semibold sm:block ${i === step ? "text-nuit-900" : "text-steel"}`}>
                  {label}
                </span>
              </li>
            ))}
          </ol>

          <div className="mt-8 rounded-3xl border border-nuit-900/10 bg-white p-6 shadow-[0_20px_50px_-30px_rgba(7,26,45,0.35)] sm:p-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -32 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                {/* ÉTAPE 1 — Activité */}
                {step === 0 && (
                  <div data-testid="config-step-1">
                    <h2 className="font-display text-xl font-bold text-nuit-900">Quelle est votre activité ?</h2>
                    <p className="mt-1.5 text-sm text-steel">
                      Nous pré-remplirons les liens les plus utiles pour votre secteur.
                    </p>
                    <div className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                      {ACTIVITIES.map((a) => (
                        <button
                          key={a}
                          onClick={() => pickActivity(a)}
                          data-testid={`config-activity-${a.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          aria-pressed={activity === a}
                          className={`rounded-2xl border px-4 py-3.5 text-left font-display text-sm font-semibold transition-colors duration-300 ${
                            activity === a
                              ? "border-electric bg-electric/10 text-nuit-900"
                              : "border-nuit-900/12 bg-brume text-nuit-900 hover:border-electric/50"
                          }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ÉTAPE 2 — Personnalisation libre */}
                {step === 1 && (
                  <div data-testid="config-step-2">
                    <h2 className="font-display text-xl font-bold text-nuit-900">Votre identité, sans limite de couleur</h2>
                    <p className="mt-1.5 text-sm text-steel">
                      L'aperçu applique automatiquement la finition premium : cadre foil, ornement
                      et symbole NFC.
                    </p>

                    <div className="mt-6 rounded-2xl bg-nuit-950 p-5">
                      <div className="mx-auto max-w-sm">
                        <CardMockup {...cardProps} shine />
                      </div>
                      <p className="mt-3 text-center text-[11px] font-medium uppercase tracking-[0.18em] text-white/50">
                        Aperçu recto — finition premium par défaut
                      </p>
                    </div>

                    <div className="mt-7 space-y-6">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="cfg-name" className="mb-1.5 block text-sm font-semibold text-nuit-900">
                            Nom de l'établissement *
                          </label>
                          <input id="cfg-name" data-testid="config-name-input" className={inputCls} value={name}
                            onChange={(e) => setName(e.target.value)} placeholder="Ex : Chez Marco" maxLength={40} />
                        </div>
                        <div>
                          <label htmlFor="cfg-slogan" className="mb-1.5 block text-sm font-semibold text-nuit-900">
                            Slogan (optionnel)
                          </label>
                          <input id="cfg-slogan" data-testid="config-slogan-input" className={inputCls} value={slogan}
                            onChange={(e) => setSlogan(e.target.value)} placeholder="Ex : Cuisine de saison" maxLength={60} />
                        </div>
                      </div>

                      <div>
                        <span className="mb-2 block text-sm font-semibold text-nuit-900">Votre logo</span>
                        <button
                          onClick={() => fileRef.current?.click()}
                          data-testid="config-logo-upload"
                          className="flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-nuit-900/20 bg-brume px-4 py-6 text-sm font-medium text-steel transition-colors hover:border-electric/50 hover:text-nuit-900"
                        >
                          {logo ? (
                            <img src={logo} alt="Aperçu de votre logo" className="h-10 w-10 rounded-lg object-contain" />
                          ) : (
                            <Upload className="h-5 w-5" />
                          )}
                          {logo ? "Logo chargé — cliquer pour changer" : "Importer mon logo (PNG, JPG, SVG — fond transparent conseillé)"}
                        </button>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden"
                          onChange={onLogo} aria-label="Importer votre logo" />
                        {logoError && (
                          <p className="mt-2 text-sm font-medium text-red-600" role="alert" data-testid="config-logo-error">{logoError}</p>
                        )}
                        <p className="mt-2 text-xs leading-relaxed text-steel">
                          L'aperçu est local : le fichier ne quitte pas votre navigateur. Vous enverrez
                          votre logo dans la conversation WhatsApp lors de la commande.
                        </p>
                      </div>

                      {/* Couleur libre */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="cfg-base" className="mb-1.5 block text-sm font-semibold text-nuit-900">
                            Couleur de la carte
                          </label>
                          <div className="flex items-center gap-2.5">
                            <input id="cfg-base" type="color" value={base} data-testid="config-color-base"
                              onChange={(e) => setBaseHex(e.target.value)}
                              className="h-11 w-14 shrink-0 cursor-pointer rounded-lg border border-nuit-900/15 bg-white p-1" />
                            <input className={`${inputCls} font-mono uppercase`} value={baseHex} data-testid="config-hex-base"
                              onChange={(e) => setBaseHex(e.target.value)} placeholder="#071A2D" maxLength={7}
                              aria-label="Code hexadécimal de la couleur de carte" />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="cfg-accent" className="mb-1.5 block text-sm font-semibold text-nuit-900">
                            Couleur secondaire / accent
                          </label>
                          <div className="flex items-center gap-2.5">
                            <input id="cfg-accent" type="color" value={accent} data-testid="config-color-accent"
                              onChange={(e) => setAccentHex(e.target.value)}
                              className="h-11 w-14 shrink-0 cursor-pointer rounded-lg border border-nuit-900/15 bg-white p-1" />
                            <input className={`${inputCls} font-mono uppercase`} value={accentHex} data-testid="config-hex-accent"
                              onChange={(e) => setAccentHex(e.target.value)} placeholder="#4C9AFF" maxLength={7}
                              aria-label="Code hexadécimal de la couleur d'accent" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <span className="mb-2 block text-sm font-semibold text-nuit-900">Palettes suggérées</span>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                          {PALETTES.map((p) => (
                            <button
                              key={p.label}
                              onClick={() => { setBaseHex(p.base); setAccentHex(p.accent); }}
                              data-testid={`config-palette-${p.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                              className="group rounded-xl border border-nuit-900/10 p-1.5 transition-colors hover:border-electric"
                              title={p.label}
                            >
                              <span className="relative block h-9 w-full overflow-hidden rounded-lg" style={{ background: gradientFrom(p.base) }}>
                                <span className="absolute bottom-1 right-1 h-3 w-3 rounded-full border border-white/60" style={{ background: p.accent }} />
                              </span>
                              <span className="mt-1 block truncate text-center text-[10px] font-semibold text-nuit-900">{p.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Style */}
                      <div>
                        <span className="mb-2 block text-sm font-semibold text-nuit-900">Style de finition</span>
                        <div className="grid gap-2.5 sm:grid-cols-2">
                          {STYLES.map((s) => (
                            <button
                              key={s.id}
                              onClick={() => pickStyle(s)}
                              data-testid={`config-style-${s.id}`}
                              aria-pressed={styleId === s.id}
                              className={`rounded-2xl border p-4 text-left transition-colors duration-300 ${
                                styleId === s.id ? "border-electric bg-electric/5" : "border-nuit-900/12 bg-brume hover:border-electric/40"
                              }`}
                            >
                              <span className="flex items-center gap-2 font-display text-sm font-bold text-nuit-900">
                                {s.label}
                                {s.id === "premium" && (
                                  <span className="rounded-full bg-electric px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                    Par défaut
                                  </span>
                                )}
                              </span>
                              <span className="mt-1 block text-xs text-steel">{s.desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Motif de fond */}
                      <div>
                        <span className="mb-2 block text-sm font-semibold text-nuit-900">Motif de fond</span>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                          {MOTIFS.map((m) => (
                            <button
                              key={m.id}
                              onClick={() => setMotifSel(m.id)}
                              data-testid={`config-motif-${m.id}`}
                              aria-pressed={motifSel === m.id}
                              className={`rounded-xl border px-2 py-2.5 text-center transition-colors duration-300 ${
                                motifSel === m.id ? "border-electric bg-electric/5" : "border-nuit-900/12 bg-brume hover:border-electric/40"
                              }`}
                            >
                              <span className="block text-xs font-bold text-nuit-900">{m.label}</span>
                              {m.id === MOTIF_BY_ACTIVITY[activity] && (
                                <span className="mt-1 inline-block rounded-full bg-electric/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-electric">
                                  Conseillé
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                        {MOTIF_BY_ACTIVITY[activity] && motifSel === "auto" && (
                          <p className="mt-2 text-xs font-medium text-electric">
                            Motif « {MOTIFS.find((m) => m.id === MOTIF_BY_ACTIVITY[activity]).label} »
                            appliqué automatiquement — conseillé pour votre activité.
                          </p>
                        )}
                      </div>

                      {/* Sur-mesure */}
                      <label
                        className="flex cursor-pointer items-start gap-3 rounded-2xl border border-electric/25 bg-electric/5 p-4"
                        data-testid="config-surmesure-label"
                      >
                        <input
                          type="checkbox"
                          checked={surMesure}
                          onChange={(e) => setSurMesure(e.target.checked)}
                          data-testid="config-surmesure-check"
                          className="mt-0.5 h-5 w-5 shrink-0 accent-electric"
                        />
                        <span className="text-sm leading-relaxed text-nuit-900">
                          <span className="flex items-center gap-1.5 font-display font-bold">
                            <Sparkles className="h-4 w-4 text-electric" />
                            Je souhaite une personnalisation sur-mesure par l'équipe Noveocard
                          </span>
                          Cadres, ornements, dégradés, mise en page, icônes : nous dessinons votre
                          visuel et vous le validons sur WhatsApp avant production.{" "}
                          <strong>Personnalisation sur-mesure incluse, validée avec vous avant production.</strong>
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* ÉTAPE 3 — Contenu digital */}
                {step === 2 && (
                  <div data-testid="config-step-3">
                    <h2 className="font-display text-xl font-bold text-nuit-900">Que doit découvrir votre client ?</h2>
                    <p className="mt-1.5 text-sm text-steel">
                      Plusieurs liens ? Ils seront regroupés sur une élégante page digitale (Carte
                      Multi-liens). Suggestions pré-cochées selon votre activité.
                    </p>
                    <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                      {LINK_CATALOG.map((l) => {
                        const active = !!links[l.key]?.enabled;
                        return (
                          <div
                            key={l.key}
                            className={`rounded-2xl border p-3.5 transition-colors duration-300 ${
                              active ? "border-electric bg-electric/5" : "border-nuit-900/12 bg-brume"
                            }`}
                          >
                            <button
                              onClick={() => toggleLink(l.key)}
                              data-testid={`config-link-${l.key}`}
                              aria-pressed={active}
                              className="flex w-full items-center justify-between gap-3"
                            >
                              <span className="flex items-center gap-2.5 font-display text-sm font-semibold text-nuit-900">
                                <l.icon className={`h-4 w-4 ${active ? "text-electric" : "text-steel"}`} />
                                {l.label}
                                {suggested.includes(l.key) && !active && (
                                  <span className="rounded-full bg-electric/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-electric">
                                    Conseillé
                                  </span>
                                )}
                              </span>
                              <span className={`h-5 w-9 rounded-full p-0.5 transition-colors duration-300 ${active ? "bg-electric" : "bg-nuit-900/15"}`}>
                                <span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-300 ${active ? "translate-x-4" : ""}`} />
                              </span>
                            </button>
                            {active && (
                              <input
                                className={`${inputCls} mt-3 !py-2.5`}
                                placeholder={l.placeholder}
                                value={links[l.key]?.value || ""}
                                onChange={(e) => setLinkValue(l.key, e.target.value)}
                                data-testid={`config-link-input-${l.key}`}
                                aria-label={`Lien ou valeur pour ${l.label}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {activeLinks.length === 0 && (
                      <p className="mt-4 text-sm font-medium text-amber-700" role="alert" data-testid="config-links-warning">
                        Sélectionnez au moins un contenu digital pour continuer.
                      </p>
                    )}
                  </div>
                )}

                {/* ÉTAPE 4 — Aperçu recto / verso */}
                {step === 3 && (
                  <div data-testid="config-step-4">
                    <h2 className="font-display text-xl font-bold text-nuit-900">Votre carte prend vie</h2>
                    <p className="mt-1.5 text-sm text-steel">
                      Recto et verso, finition premium. Aperçu indicatif : le visuel final est affiné
                      et validé avec vous avant production.
                    </p>
                    <div className="mt-8 rounded-3xl bg-nuit-950 px-5 py-10">
                      <CardPair {...cardProps} className="mx-auto max-w-xl" />
                      <p className="mt-8 text-center text-xs font-medium uppercase tracking-[0.18em] text-white/50">
                        PVC 86 × 54 mm · NFC · Recto & verso
                      </p>
                    </div>
                    <div className="phone-mock mx-auto mt-8 h-72 w-36 !border-nuit-900/15 p-1.5">
                      <div className="flex h-full flex-col items-center gap-1.5 overflow-hidden rounded-[1.4rem] bg-white p-2.5 pt-4 text-nuit-900">
                        {logo ? (
                          <img src={logo} alt="" className="h-8 w-8 rounded-lg object-contain" />
                        ) : (
                          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-nuit-900 font-display text-[10px] font-bold text-white">
                            {(name || "V").charAt(0).toUpperCase()}
                          </span>
                        )}
                        <p className="max-w-full truncate font-display text-[10px] font-bold">{name || "Votre Établissement"}</p>
                        <div className="mt-1 flex w-full flex-col gap-1">
                          {activeLinks.slice(0, 6).map((l) => (
                            <span key={l.key} className="flex items-center gap-1.5 rounded-md bg-brume px-2 py-1 text-[8px] font-medium">
                              <l.icon className="h-2.5 w-2.5 text-electric" /> {l.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="mt-4 text-center text-xs font-medium uppercase tracking-[0.18em] text-steel">
                      Votre page digitale, ouverte d'un geste
                    </p>
                  </div>
                )}

                {/* ÉTAPE 5 — Récapitulatif */}
                {step === 4 && (
                  <div data-testid="config-step-5">
                    <h2 className="font-display text-xl font-bold text-nuit-900">Votre récapitulatif</h2>

                    <div className="mt-6">
                      <span className="mb-2 block text-sm font-semibold text-nuit-900">Choisissez votre offre</span>
                      <div className="grid gap-2.5 sm:grid-cols-3">
                        {OFFERS.filter((o) => o.id !== "sur-mesure").map((o) => {
                          const selected = effectiveOffer.id === o.id;
                          return (
                            <button
                              key={o.id}
                              onClick={() => setOfferId(o.id)}
                              data-testid={`config-offer-${o.id}`}
                              aria-pressed={selected}
                              className={`rounded-2xl border p-4 text-left transition-colors duration-300 ${
                                selected ? "border-electric bg-electric/5" : "border-nuit-900/12 bg-brume hover:border-electric/40"
                              }`}
                            >
                              <span className="font-display text-sm font-bold text-nuit-900">{o.name}</span>
                              <span className="mt-1 block font-display text-2xl font-bold text-electric">{o.price}</span>
                              <span className="mt-1 block text-xs text-steel">
                                {o.id === "pack4" ? "4 cartes personnalisées" : o.id === "multi" ? "Page digitale multi-liens" : "1 lien digital"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {activeLinks.length > 1 && !offerId && (
                        <p className="mt-2 text-xs font-medium text-electric">
                          Plusieurs liens sélectionnés : la Carte Multi-liens est pré-sélectionnée pour vous.
                        </p>
                      )}
                    </div>

                    <dl className="mt-7 space-y-3 rounded-2xl bg-brume p-5 text-sm">
                      {[
                        ["Type de carte", effectiveOffer.name],
                        ["Quantité", quantity],
                        ["Prix de l'offre", effectiveOffer.price],
                        ["Activité", activity],
                        ["Nom sur la carte", name],
                        slogan && ["Slogan", slogan],
                        ["Couleur de carte", base],
                        ["Couleur secondaire", accent],
                        ["Style", style.label],
                        ["Motif de fond", motifLabel || "Aucun"],
                        ["Sur-mesure Noveocard", surMesure ? "Oui — visuel validé avant production" : "Non"],
                        ["Logo", logo ? "Aperçu chargé — à envoyer dans la conversation" : "À envoyer dans la conversation"],
                        ["Contenu digital", activeLinks.map((l) => l.label).join(", ")],
                      ].filter(Boolean).map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-4 border-b border-nuit-900/8 pb-2.5 last:border-0 last:pb-0">
                          <dt className="font-semibold text-nuit-900">{k}</dt>
                          <dd className="text-right text-steel">{v}</dd>
                        </div>
                      ))}
                    </dl>

                    <div className="mt-7 flex flex-col items-center gap-3">
                      <a
                        href={waLink(recapMessage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="config-order-whatsapp"
                        className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-electric px-6 py-4 text-center font-display text-base font-semibold text-white shadow-[0_10px_30px_-8px_rgba(22,119,255,0.55)] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-electric-600 sm:w-auto sm:whitespace-nowrap sm:px-8 sm:text-lg"
                      >
                        <WhatsAppIcon className="h-5 w-5" />
                        Commander ma carte sur WhatsApp
                      </a>
                      <p className="max-w-md text-center text-xs leading-relaxed text-steel">
                        Le message s'ouvre pré-rempli avec tout votre récapitulatif. WhatsApp ne permet
                        pas de joindre automatiquement un fichier : vous pourrez envoyer votre logo
                        directement dans la conversation. Le règlement se fait ensuite directement avec
                        Noveocard, hors du site. {RESPONSE_TIME}.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <SurMesure />

      {/* Barre de navigation collante (mobile-first) */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-nuit-900/10 bg-white/95 backdrop-blur-xl">
        <div className="container-nv flex max-w-3xl items-center justify-between gap-3 py-3.5">
          <button
            onClick={() => goTo(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="config-prev-btn"
            className="inline-flex items-center gap-2 rounded-full border border-nuit-900/15 px-5 py-3 font-display text-sm font-semibold text-nuit-900 transition-opacity disabled:opacity-30"
          >
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
          <span className="text-xs font-semibold text-steel">
            Étape {step + 1} / {STEPS.length} — {STEPS[step]}
          </span>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => canContinue && goTo(step + 1)}
              disabled={!canContinue}
              data-testid="config-next-btn"
              className="inline-flex items-center gap-2 rounded-full bg-nuit-900 px-5 py-3 font-display text-sm font-semibold text-white transition-all enabled:hover:bg-nuit-800 disabled:opacity-30"
            >
              Continuer <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <span className="w-[110px]" />
          )}
        </div>
      </div>
    </>
  );
}
